/**
 * The ticket status vocabulary as the SPA holds it: how a status is coloured, and the
 * order to lay statuses out in before the server has said.
 */

/** Every view that renders a status Chip reads its colour from here. */
export const STATUS_COLOUR: Record<string, 'default' | 'primary' | 'success'> = {
  'Not started': 'default',
  'In progress': 'primary',
  Done: 'success',
  // Grey like `Not started`, deliberately: closed work is not an achievement, and giving
  // it a colour of its own would make a decision to stop as loud as a delivery. The label
  // carries the difference here; the server's `isSettled` (`src/domain.ts`) carries it
  // everywhere it changes an answer, and the SPA deliberately holds no copy of it.
  Closed: 'default',
};

/**
 * The FALLBACK column order for a view that lays out one column per status, used only
 * until `/v1/meta` answers.
 *
 * The server owns this vocabulary (`src/domain.ts` STATUSES), and every view that renders
 * columns takes it from there — a status the server has and a view does not renders no
 * column at all, and its tickets are then counted in a header while appearing nowhere
 * below it.
 *
 * Written out rather than derived from `STATUS_COLOUR`'s keys. The two answer different
 * questions: the colour map tolerates keys for statuses that are retired or not yet
 * served, because an unknown status just renders grey — here an extra key would be a
 * phantom column on the first paint of every epic page.
 */
export const STATUS_ORDER: string[] = ['Not started', 'In progress', 'Done', 'Closed'];

/**
 * The denominator a progress fraction is measured against: every ticket not closed.
 *
 * ⚠️ Named for what it is FOR, because it is NOT the count of outstanding work — it
 * includes every finished ticket. `outstanding` means neither `Done` nor `Closed`
 * everywhere else in this system (`isSettled` in `src/domain.ts`, and the `epics_get`
 * docstring), and reusing the word here for a different set is how someone reaches for
 * this by name and gets "how much is left" wrong.
 *
 * Closed work leaves the denominator rather than joining `done`: counting it as done
 * claims a delivery that never happened, and leaving it in makes an epic that dropped
 * four of ten read 6/10 forever, never reaching its own finish.
 *
 * One definition, because both the list card and the epic header show a fraction and they
 * must not answer differently. Why it lives in the SPA at all: ARCHITECTURE.md, DWLF-194.
 */
export const progressDenominator = (counts: {
  ticketCount: number;
  closedCount: number;
}): number => counts.ticketCount - counts.closedCount;
